/* eslint-disable react/style-prop-object */
import React, { useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import { FormattedMessage, FormattedNumber } from 'react-intl';

const DECIMALS = 3;

export interface ICompareMetricsTableProps {
  header: string;
  data: ICompareTableMetrics;
  compareHeader: string;
  compareData: ICompareTableMetrics;
}

export interface ICompareTableMetrics {
  handling: ICompareTableMetricsRow;
  transport: ICompareTableMetricsRow;
  receiving: ICompareTableMetricsRow;
  containers: ICompareTableMetricsRow;
  total_cost: ICompareTableMetricsRow;
  metal_refunds: ICompareTableMetricsRow;
  recycle_fees: ICompareTableMetricsRow;
  total_income: ICompareTableMetricsRow;
}

export interface ICompareTableMetricsRow {
  avg_metric: number;
  eur: number;
  eurKg: number;
  pct: number;
}

const COLUMNS = [
  'description',
  'eur',
  'eurKg',
  'pct'
];

const CompareMetricsTable: React.FunctionComponent<ICompareMetricsTableProps> = (props: ICompareMetricsTableProps) => {
  const [metrics, setMetrics] = useState({} as ICompareTableMetrics);
  const [compare, setCompare] = useState({} as ICompareTableMetrics);

  useEffect(() => {
    setMetrics(props.data);
  }, [props.data, props.header]);

  useEffect(() => {
    setCompare(props.compareData);
  }, [props.compareData, props.compareHeader]);


  const getTableRange = () => {
    const range: JSX.Element[] = [];
    range.push(<th className="empty" key={"empty"}></th>);
    range.push(<th colSpan={3} className="report-compare range-header" key={"report-compare-range"}>{props.header}</th>);
    range.push(<th colSpan={2} className="center" key={"report-compare-eurKg"}> <FormattedMessage id={'sortsummaryreports.settings.eurkg'}/></th>);
    range.push(<th colSpan={2} className="center" key={"report-compare-eur"}> <FormattedMessage id={'sortsummaryreports.settings.eur'}/></th>);
    range.push(<th colSpan={3} className="compare-report range-header" key={"compare-report-range"}>{props.compareHeader}</th>);
    return range;
  }

  const getTableHeaders = () => {
    const headers: JSX.Element[] = [];
    COLUMNS.forEach((name) => {
      const classes = ['header'];
      let className = "center";
      if(name === 'description') className = "";
      headers.push(
        <th key={name} className={className}>
          <span className={classes.join(' ')}>
            <FormattedMessage id={`comparereport.datatable.${name}`}/>
          </span>
        </th>
      );
    })
    headers.push(
      <th key={"eurKg-pct"} className="center">
        <span className="header">
          <FormattedMessage id={'comparereport.datatable.pct'}/>
      </span></th>);
    headers.push(
      <th key={"eurKg-difference"} className="center">
        <span className="header">
          <FormattedMessage id={'comparereport.datatable.difference'}/>
      </span></th>);
    headers.push(
      <th key={"eur-pct"} className="center">
        <span className="header">
          <FormattedMessage id={'comparereport.datatable.pct'}/>
        </span></th>);
    headers.push(
      <th key={"eur-difference"} className="center">
        <span className="header">
          <FormattedMessage id={'comparereport.datatable.difference'}/>
        </span></th>);
    COLUMNS.forEach((name, index) => {
      const classes = ['header'];
      if(name !== 'description')
        headers.push(
          <th key={name+index} className="center">
            <span className={classes.join(' ')}>
            <FormattedMessage id={`comparereport.datatable.${name}`}/>
          </span></th>
        );
    })
    return headers;
  }

  const getDataTable = ( ) => {
    const options: { }[] = [];
    if (metrics || compare) {
      let keys = Object.keys(metrics);
      keys.forEach((key) => {
        let metric = metrics[key as keyof typeof metrics];
        let comp = compare[key as keyof typeof compare];
        let bgColor = "";
        if(key.includes("total")) {
          bgColor = "total ";
        }

        let metricEur = 0;
        let metricEurKg = 0;
        let metricPct = 0;
        let meurkg = 0;
        let meur = 0;
        if(metric) {
          metricEur = metric.eur ? metric.eur : 0;
          metricEurKg = (metric.eurKg !== null && metric.eurKg !== undefined) ? metric.eurKg : 0;
          metricPct = (metric.pct !== null && metric.pct !== undefined) ? (metric.pct) : 0;
          meurkg = metric.eurKg ? metric.eurKg : 0;
          meur = metric.eur ? metric.eur : 0;
        }
        let compEur = 0;
        let compEurKg = 0;
        let compPct = 0;
        let ceurkg = 0;
        let ceur = 0;
        if(comp) {
          compEur = comp.eur ? comp.eur : 0;
          compEurKg = (comp.eurKg !== null && comp.eurKg !== undefined) ? comp.eurKg : 0;
          compPct = (comp.pct !== null && comp.pct !== undefined) ? (comp.pct) : 0;
          ceurkg = comp.eurKg ? comp.eurKg : 0;
          ceur = comp.eur ? comp.eur : 0;
        }
        let eurKgDiff = meurkg - ceurkg;
        let eurKgPct = (ceurkg ? (eurKgDiff/ceurkg) : 0);

        let eurDiff = meur - ceur;
        let eurPct = (ceur ? (eurDiff/ceur) : 0);

        options.push(
            <tr key={key}>
              <td className={ bgColor }><FormattedMessage id={`comparereport.datatable.${key.toLowerCase()}`}/></td>
              <td className={ bgColor + "right"} ><FormattedNumber value={metricEur} maximumFractionDigits={0} minimumFractionDigits={0}/></td>
              <td className={ bgColor + "right"} ><FormattedNumber value={metricEurKg} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></td>
              <td className={ bgColor + "right"} ><FormattedNumber value={metricPct} style={"percent"} maximumFractionDigits={0} minimumFractionDigits={0}/></td>

              <td className={ bgColor + "right" + getSignedStyleClass(eurKgPct) }><FormattedNumber value={eurKgPct} style={"percent"} maximumFractionDigits={0} minimumFractionDigits={0}/></td>
              <td className={ bgColor + "right" + getSignedStyleClass(eurKgDiff) }><FormattedNumber value={eurKgDiff} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></td>
              <td className={ bgColor + "right" + getSignedStyleClass(eurPct) }><FormattedNumber value={eurPct} style={"percent"} maximumFractionDigits={0} minimumFractionDigits={0}/></td>
              <td className={ bgColor + "right" + getSignedStyleClass(eurDiff) }><FormattedNumber value={eurDiff} maximumFractionDigits={0} minimumFractionDigits={0}/></td>

              <td className={ bgColor + "right"} ><FormattedNumber value={compEur} maximumFractionDigits={0} minimumFractionDigits={0}/></td>
              <td className={ bgColor + "right"} ><FormattedNumber value={compEurKg} maximumFractionDigits={DECIMALS} minimumFractionDigits={DECIMALS}/></td>
              <td className={ bgColor + "right"} ><FormattedNumber value={compPct} style={"percent"} maximumFractionDigits={0} minimumFractionDigits={0}/></td>
            </tr>
          );
      });
    }
    return options;
  }

  const getSignedStyleClass = (value: any) => {
    if(value === null || value === undefined) return "";
    if(Math.sign(value) === 1) return " positive";
    else if(Math.sign(value) === -1) return " negative";
    else return "";
  }

  return (
    <div className="CompareReportTable MetricsTable">
      <Table>
        <thead className="reportDataHeader table">
        <tr>
          { getTableRange() }
        </tr>
        <tr className="table-headers table-bordered">
          { getTableHeaders() }
        </tr>
        </thead>
        <tbody className="reportData table table-bordered">
          { getDataTable() }
        </tbody>
      </Table>
    </div>
  )
}

export default CompareMetricsTable;
